import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

/**
 * SidebarLabelsContext — per-agency renames for sidebar tabs AND page
 * H1 titles. One override map keyed by:
 *   • route path (`/clients`, `/settings/adhoc-requesters`) — for
 *     both the sidebar leaf entry and the page's H1.
 *   • `section:<slug>` — for section headers in the sidebar.
 *
 * Missing keys fall back to the code default. Loaded once at app boot;
 * mutations invalidate via `refresh()`.
 */
const Ctx = createContext({
  overrides: {},
  refresh: async () => {},
  loading: true,
  save: async (_next) => {},
});

export function SidebarLabelsProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/agency/sidebar-labels");
      setOverrides((data && data.overrides) || {});
    } catch { /* silent — unauthenticated boot / missing agency ok */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (next) => {
    // Backend strips empty values, so caller can pass "" to clear a key.
    const { data } = await api.put("/agency/sidebar-labels", { overrides: next });
    setOverrides((data && data.overrides) || {});
    return data;
  }, []);

  return (
    <Ctx.Provider value={{ overrides, refresh, save, loading }}>
      {children}
    </Ctx.Provider>
  );
}

/** Look up the current agency's override for `key`, falling back to
 *  `fallback` if none exists. `key` is typically a route path
 *  (`/clients`) or `section:<slug>` for section headers. */
export function useSidebarLabel(key, fallback) {
  const { overrides } = useContext(Ctx);
  if (!key) return fallback;
  const v = overrides[key];
  return (v && String(v).trim()) || fallback;
}

export function useSidebarLabelsAll() {
  return useContext(Ctx);
}
